// components/ReglementInterieur.js
import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaFileContract,
  FaImage,
  FaPrint,
  FaShieldAlt,
} from "react-icons/fa";

import { getSettings } from "../../services/settingsServices";
import useScrollToTop from "../../hooks/useScollToTop";
import { toAbsolutePhotoUrl } from "../../services/settingsPhotoServices";

const FALLBACK = "/img/article_placeholder.jpg";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const ReglementSkeleton = () => {
  return (
    <div className="min-h-screen bg-slate-50 pt-[calc(var(--site-navbar-h)+48px)] dark:bg-slate-950">
      <div className="mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mx-auto h-9 w-48 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="mx-auto mt-6 h-12 w-2/3 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
          <div className="mx-auto mt-5 h-5 w-3/4 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        </div>

        <div className="mt-12 overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white shadow-xl dark:border-white/10 dark:bg-slate-900">
          <div className="h-72 animate-pulse bg-slate-200 dark:bg-slate-800" />
          <div className="space-y-4 p-6 sm:p-8">
            <div className="h-4 w-full animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
            <div className="h-4 w-11/12 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
            <div className="h-4 w-4/5 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
          </div>
        </div>
      </div>
    </div>
  );
};

const EmptyState = () => {
  return (
    <div className="rounded-[2rem] border border-dashed border-slate-300 bg-white/80 p-10 text-center shadow-sm dark:border-white/10 dark:bg-white/5">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
        <FaFileContract />
      </div>

      <h2 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
        Règlement intérieur non renseigné
      </h2>

      <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
        Le contenu du règlement intérieur apparaîtra ici dès qu’il sera ajouté
        dans les paramètres du club.
      </p>
    </div>
  );
};

const ImageCard = ({ src, alt }) => {
  if (!src) return null;

  return (
    <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-3 shadow-lg dark:border-white/10 dark:bg-slate-900">
      <div className="flex min-h-[260px] items-center justify-center rounded-[1.5rem] bg-slate-50 dark:bg-white/5">
        <img
          src={src}
          alt={alt}
          className="max-h-[650px] w-full object-contain"
          loading="lazy"
          onError={(event) => {
            event.currentTarget.src = FALLBACK;
          }}
        />
      </div>
    </div>
  );
};

const ReglementInterieur = () => {
  useScrollToTop();

  const navigate = useNavigate();

  const [settings, setSettings] = useState({
    nom_club: "",
    reglement_interieur: "",
    reglement_interieur_url_header: "",
    reglement_interieur_url_firstimage: "",
    reglement_interieur_url_secondimage: "",
  });

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const settingsData = await getSettings();

        if (!mounted) return;

        if (Array.isArray(settingsData) && settingsData.length > 0) {
          setSettings((previous) => ({
            ...previous,
            ...settingsData[0],
          }));
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des données:", error);

        if (mounted) {
          setErrorMsg("Impossible de charger le règlement intérieur.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const headerSrc = useMemo(() => {
    const raw = settings.reglement_interieur_url_header?.trim();
    return toAbsolutePhotoUrl(raw) || "";
  }, [settings.reglement_interieur_url_header]);

  const firstSrc = useMemo(() => {
    const raw = settings.reglement_interieur_url_firstimage?.trim();
    return toAbsolutePhotoUrl(raw) || "";
  }, [settings.reglement_interieur_url_firstimage]);

  const secondSrc = useMemo(() => {
    const raw = settings.reglement_interieur_url_secondimage?.trim();
    return toAbsolutePhotoUrl(raw) || "";
  }, [settings.reglement_interieur_url_secondimage]);

  const safeHtml = useMemo(() => {
    return DOMPurify.sanitize(settings.reglement_interieur || "", {
      USE_PROFILES: {
        html: true,
      },
      FORBID_ATTR: ["style"],
    });
  }, [settings.reglement_interieur]);

  const hasContent = Boolean(safeHtml?.trim());
  const hasExtraImages = Boolean(firstSrc || secondSrc);

  const handlePrint = () => {
    window.print();
  };

  if (loading) return <ReglementSkeleton />;

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-50 pt-[calc(var(--site-navbar-h)+48px)] text-slate-950 dark:bg-slate-950 dark:text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.14)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <style>{`
        @media print {
          nav,
          footer,
          .no-print {
            display: none !important;
          }

          main {
            padding-top: 0 !important;
            background: #ffffff !important;
          }

          .print-card {
            box-shadow: none !important;
            border: none !important;
          }
        }
      `}</style>

      <div className="relative mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="no-print mb-8 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm backdrop-blur transition hover:-translate-y-0.5 hover:border-primary/30 hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
          >
            <FaArrowLeft className="text-xs" />
            Retour
          </button>

          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:brightness-110"
          >
            <FaPrint className="text-sm" />
            Imprimer
          </button>
        </div>

        <section className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaShieldAlt />
            Document du club
          </div>

          <h1 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Règlement{" "}
            <span className="text-primary">intérieur</span>
          </h1>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Retrouvez les règles de fonctionnement, les informations pratiques
            et les engagements nécessaires à la bonne vie du club.
          </p>

          {settings.nom_club && (
            <p className="mt-4 text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
              {settings.nom_club}
            </p>
          )}
        </section>

        {errorMsg && (
          <div className="mt-12 rounded-[1.5rem] border border-red-200 bg-red-50 p-4 text-center text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
            {errorMsg}
          </div>
        )}

        {!errorMsg && !hasContent && !headerSrc && !hasExtraImages && (
          <div className="mt-12">
            <EmptyState />
          </div>
        )}

        {!errorMsg && (hasContent || headerSrc || hasExtraImages) && (
          <article className="print-card mt-12 overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white shadow-2xl dark:border-white/10 dark:bg-slate-900">
            {headerSrc && (
              <div className="border-b border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-white/5">
                <div className="flex min-h-[260px] items-center justify-center overflow-hidden rounded-[2rem] bg-white dark:bg-slate-950">
                  <img
                    src={headerSrc}
                    alt="En-tête du règlement intérieur"
                    className="max-h-[700px] w-full object-contain"
                    loading="lazy"
                    onError={(event) => {
                      event.currentTarget.src = FALLBACK;
                    }}
                  />
                </div>
              </div>
            )}

            {hasContent && (
              <div className="p-6 sm:p-8 lg:p-10">
                <div
                  className={cn(
                    "prose prose-slate max-w-none dark:prose-invert",
                    "prose-p:leading-8 prose-p:text-slate-600 dark:prose-p:text-slate-300",
                    "prose-headings:font-barlow prose-headings:font-bold prose-headings:text-slate-950 dark:prose-headings:text-white",
                    "prose-a:font-bold prose-a:text-primary",
                    "prose-strong:text-slate-950 dark:prose-strong:text-white",
                    "dark:[&_strong]:!text-white dark:[&_b]:!text-white",
                    "dark:[&_strong]:font-bold dark:[&_b]:font-bold"
                  )}
                  dangerouslySetInnerHTML={{
                    __html: safeHtml,
                  }}
                />
              </div>
            )}

            {hasExtraImages && (
              <div className="border-t border-slate-200 bg-slate-50 p-6 dark:border-white/10 dark:bg-slate-950/40 sm:p-8">
                <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 shadow-sm dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                  <FaImage className="text-primary" />
                  Documents complémentaires
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <ImageCard src={firstSrc} alt="Première image du règlement" />
                  <ImageCard src={secondSrc} alt="Deuxième image du règlement" />
                </div>
              </div>
            )}

            <div
              aria-hidden
              className="h-1.5 w-full bg-gradient-to-r from-transparent via-primary to-transparent opacity-90"
            />
          </article>
        )}
      </div>
    </main>
  );
};

export default ReglementInterieur;